import { getPublicKeys, PaymentSystem } from '@web-solutions/react-billing';

// Shared config for the Stripe intro one-time charge that wraps the
// trial-then-subscription flow. Today's actual charge ($0.99) is set up here
// and consumed by:
//   - PaymentProcessor (charges the intro via createOneClickPayment)
//   - ProductInfo / SimpleView (renders breakdown in the paywall modal)
//   - ApplePayButton (shows correct today amount in Apple Pay sheet)
export const STRIPE_INTRO_PAYMENT = {
  amount: 0.99,
  currency: 'USD',
  productCode: 'FoodZee Ethnicity Trial',
  productId: 'FoodZee Ethnicity Trial',
  priceId: 'price_1TWyoTGhKvuDkyq1sxflupm3',
  trialPeriodDays: '3',
  trigger: 'stripe_intro_trial',
} as const;

// Stripe subscriptions don't expose the intro/trial price on the product
// itself (we charge it via a separate one-time payment + trial_period_days).
// To render the breakdown UI we need to inject those fields on the fly.
// Try several fields to find the subscription's regular price. Remote-config
// often has `amount: "0.00"` (placeholder), so we can't trust the nullish
// coalescing alone — we prefer the value from Stripe (priceAmount) and fall
// back to anything numeric we can extract from the product.
const resolveFullAmount = (product: Record<string, any>): number => {
  const candidates = [
    product.priceAmount,
    product.amount,
    product.trialPriceAmount, // sometimes only trial info is present
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  // Last resort: parse a "$24.99" style string from textLineThrough/priceText.
  for (const s of [product.textLineThrough, product.priceText, product.price]) {
    if (typeof s !== 'string') continue;
    const parsed = Number(s.replace(/[^\d.]/g, ''));
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return NaN;
};

export const enrichStripeIntroProduct = <P extends Record<string, any>>(
  product: P | undefined | null,
  overrideIntroAmount?: number,
): P | undefined | null => {
  if (!product) return product;

  const introAmount = overrideIntroAmount ?? STRIPE_INTRO_PAYMENT.amount;
  const fullAmount = resolveFullAmount(product);

  if (!Number.isFinite(fullAmount) || fullAmount < introAmount) {
    return product;
  }

  const saved = Math.round((fullAmount - introAmount) * 100) / 100;
  const discountPct = Math.round((saved / fullAmount) * 100);

  return {
    ...product,
    isTrial: true,
    isIntroductory: true,
    trialPrice: `$${introAmount.toFixed(2)}`,
    trialPriceAmount: introAmount,
    trial_price_amount: String(introAmount),
    discount: discountPct,
    savedPrice: `$${saved.toFixed(2)}`,
  };
};

type StripePaymentIntent = {
  id?: string;
  status?: string;
};

export const confirmStripePaymentIntent = async (clientSecret?: string): Promise<StripePaymentIntent | null> => {
  if (!clientSecret) {
    return null;
  }

  const stripePublicKey = getPublicKeys()?.stripe?.public_key;
  const stripeFactory = (window as any).Stripe;

  if (!stripePublicKey || !stripeFactory) {
    throw new Error('Stripe is not initialized');
  }

  const stripe = stripeFactory(stripePublicKey);
  const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);

  if (error) {
    throw error;
  }

  return paymentIntent || null;
};

// ---------------------------------------------------------------------------
// Which config products are rendered as selectable cards.
//
// A Stripe plan is a PAIR of config entries: the trial (charged today as a
// one-time intro payment) and the subscription created after it. By convention
// the list is ordered trials first, their subscriptions second, same index:
//
//   [ trialA, trialB,   subForA, subForB ]
//      0       1          2        3          → trialA↔subForA, trialB↔subForB
//
// Subscription entries are never rendered — they only supply the regular price
// for the "$0.99 today, then $9.99 / week" breakdown.
//
// Two explicit overrides exist for configs that can't keep that order:
//   - `isIntroCard: true`        — marks which entries are rendered as cards;
//   - `subscriptionPriceId`      — names the card's subscription price directly.
// (Ported from the FoodZee build.)

type AnyProduct = Record<string, any>;

export const getLinkedSubscriptionId = (product?: AnyProduct | null): string | undefined =>
  product?.subscriptionPriceId || product?.subscriptionStripeId || product?.linkedSubscriptionId || undefined;

const matchesProductId = (product: AnyProduct | undefined | null, id?: string) => (
  !!id && (product?.id === id || product?.stripeId === id || product?.productCode === id)
);

/**
 * Splits the ordered list into trial cards and the subscriptions they lead to.
 * Returns null when the list can't be split that way (odd length), so callers
 * fall back to showing everything.
 */
const splitTrialsAndSubscriptions = <P extends AnyProduct>(products: P[]) => {
  if (products.length < 2 || products.length % 2 !== 0) {
    return null;
  }

  const half = products.length / 2;

  return {
    trials: products.slice(0, half),
    subscriptions: products.slice(half),
  };
};

type PlanCardsOptions = {
  /** The positional split is a Stripe-only convention — other systems show every product. */
  paymentSystem?: string;
};

/**
 * Products to render as selectable cards: the trials only. The subscription half
 * is hidden — it is charged only after the trial charge succeeds.
 */
export const selectStripePlanCards = <P extends AnyProduct>(
  products: P[] = [],
  { paymentSystem }: PlanCardsOptions = {},
): P[] => {
  // Explicit marking wins over everything: only the flagged trials are cards.
  const marked = products.filter((product) => product?.isIntroCard);

  if (marked.length) {
    return marked;
  }

  // Explicit pairing: hide everything referenced as somebody's subscription.
  const linkedIds = products
    .map((product) => getLinkedSubscriptionId(product))
    .filter(Boolean) as string[];

  if (linkedIds.length) {
    const cards = products.filter((product) => !linkedIds.some((id) => matchesProductId(product, id)));
    return cards.length ? cards : products;
  }

  if (paymentSystem !== PaymentSystem.STRIPE) {
    return products;
  }

  return splitTrialsAndSubscriptions(products)?.trials || products;
};
